<template>
  <Teleport to="body">
    <div
      class="toast-container"
      :class="containerClasses"
      :style="{ zIndex }"
      role="region"
      aria-live="polite"
      aria-label="Notifications"
    >
      <TransitionGroup
        enter-active-class="toast-enter-active"
        enter-from-class="toast-enter-from"
        enter-to-class="toast-enter-to"
        leave-active-class="toast-leave-active"
        leave-from-class="toast-leave-from"
        leave-to-class="toast-leave-to"
        move-class="toast-move"
      >
        <BaseToast
          v-for="toast in toasts"
          :key="toast.id"
          :id="toast.id"
          :type="toast.type"
          :variant="toast.variant"
          :title="toast.title"
          :message="toast.message"
          :duration="toast.duration"
          :persistent="toast.persistent"
          :show-close-button="toast.showCloseButton"
          :show-progress="toast.showProgress"
          :show-icon="toast.showIcon"
          :aria-label="toast.ariaLabel"
          @dismiss="handleToastDismiss"
          @click="handleToastClick"
        >
          <template v-if="toast.slotContent" #default>
            <component v-if="typeof toast.slotContent === 'object'" :is="toast.slotContent" />
            <span v-else>{{ toast.slotContent }}</span>
          </template>
          <template v-if="toast.slotIcon" #icon>
            <component :is="toast.slotIcon" />
          </template>
        </BaseToast>
      </TransitionGroup>
    </div>
  </Teleport>
</template>

<script setup lang="ts">
import { computed, ref, watch, nextTick, onUnmounted } from 'vue'
import BaseToast from './BaseToast.vue'
import { OVERLAY_Z_TOAST } from '@/lib/ui/overlayLayers'

interface ToastItem {
  id: string
  type: 'success' | 'error' | 'warning' | 'info' | 'custom'
  /** NOTIF-003: `notification` renders the quiet in-app card chrome. */
  variant?: 'default' | 'notification'
  title?: string
  message?: string
  duration?: number
  persistent?: boolean
  showCloseButton?: boolean
  showProgress?: boolean
  showIcon?: boolean
  ariaLabel?: string
  slotContent?: any
  slotIcon?: any
}

interface Props {
  position?: 'top-right' | 'top-left' | 'bottom-right' | 'bottom-left' | 'top-center' | 'bottom-center'
  maxToasts?: number
  /** Stacking order. Defaults above every modal tier — see src/lib/ui/overlayLayers.ts. */
  zIndex?: number
}

const props = withDefaults(defineProps<Props>(), {
  position: 'top-right',
  maxToasts: 5,
  zIndex: OVERLAY_Z_TOAST
})

const emit = defineEmits<{
  'toast-dismiss': [id: string]
  'toast-click': [id: string, event: MouseEvent]
}>()

// State
const toasts = ref<ToastItem[]>([])

// Computed
const containerClasses = computed(() => [
  'toast-container',
  `toast-container--${props.position}`
])

// Debug: Watch toasts changes (commented out for production)
// watch(toasts, (newToasts) => {
//   console.log('BaseToastContainer: toasts changed', newToasts.length, newToasts)
//   // Also log the DOM elements
//   nextTick(() => {
//     const toastElements = document.querySelectorAll('.toast')
//     console.log('BaseToastContainer: DOM toasts found', toastElements.length)
//   })
// }, { deep: true })

// Methods
const addToast = (toast: Omit<ToastItem, 'id'>) => {
  const id = `toast-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`
  const newToast: ToastItem = {
    id,
    ...toast
  }

  // console.log('BaseToastContainer: Adding toast', { id, toast: newToast })

  // Add to beginning of array (newest first)
  toasts.value.unshift(newToast)

  // Remove oldest toasts if we exceed maxToasts
  if (toasts.value.length > props.maxToasts) {
    toasts.value = toasts.value.slice(0, props.maxToasts)
  }

  return id
}

const removeToast = (id: string) => {
  const index = toasts.value.findIndex(toast => toast.id === id)
  if (index !== -1) {
    toasts.value.splice(index, 1)
  }
}

const clearAllToasts = () => {
  toasts.value = []
}

const removeToastsWhere = (predicate: (toast: ToastItem) => boolean) => {
  toasts.value = toasts.value.filter(toast => !predicate(toast))
}

const handleToastDismiss = (id?: string) => {
  if (id) {
    removeToast(id)
    emit('toast-dismiss', id)
  }
}

const handleToastClick = (event: MouseEvent) => {
  // Find the toast that was clicked
  const toastElement = (event.target as HTMLElement).closest('.toast')
  if (toastElement) {
    const toastId = toastElement.getAttribute('data-toast-id')
    if (toastId) {
      emit('toast-click', toastId, event)
    }
  }
}

// Expose methods for external use
defineExpose({
  addToast,
  removeToast,
  clearAllToasts,
  removeToastsWhere,
  toasts
})
</script>

<style scoped>
.toast-container {
  position: fixed;
  /* Fallback only — the live value is bound inline from the zIndex prop. */
  z-index: var(--z-toast, 10100);
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  padding: 1rem;
  pointer-events: none;
}

/* Position variants */
.toast-container--top-right {
  top: 0;
  right: 0;
}

.toast-container--top-left {
  top: 0;
  left: 0;
}

.toast-container--bottom-right {
  bottom: 0;
  right: 0;
  flex-direction: column-reverse;
}

.toast-container--bottom-left {
  bottom: 0;
  left: 0;
  flex-direction: column-reverse;
}

.toast-container--top-center {
  top: 0;
  left: 50%;
  transform: translateX(-50%);
}

.toast-container--bottom-center {
  bottom: 0;
  left: 50%;
  transform: translateX(-50%);
  flex-direction: column-reverse;
}

/* Enable pointer events for toasts */
.toast-container :deep(.toast) {
  pointer-events: auto;
}

/* Transitions */
.toast-enter-active,
.toast-leave-active {
  
}

.toast-enter-from {
  opacity: 0;
  transform: translateX(100%);
}

.toast-enter-to {
  opacity: 1;
  transform: translateX(0);
}

.toast-leave-from {
  opacity: 1;
  transform: translateX(0);
}

.toast-leave-to {
  opacity: 0;
  transform: translateX(100%);
}

.toast-move {
  
}

/* Position-specific animations */
.toast-container--top-left .toast-enter-from,
.toast-container--bottom-left .toast-enter-from {
  transform: translateX(-100%);
}

.toast-container--top-center .toast-enter-from,
.toast-container--bottom-center .toast-enter-from {
  transform: translateY(-100%);
}

.toast-container--top-left .toast-leave-to,
.toast-container--bottom-left .toast-leave-to {
  transform: translateX(-100%);
}

.toast-container--top-center .toast-leave-to,
.toast-container--bottom-center .toast-leave-to {
  transform: translateY(-100%);
}

/* Responsive adjustments */
@media (max-width: 640px) {
  .toast-container {
    padding: 0.75rem;
    gap: 0.5rem;
  }
  
  .toast-container--top-center,
  .toast-container--bottom-center {
    left: 0;
    right: 0;
    transform: none;
  }
}
</style>
